import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  canManageProperties,
  canRefundBond,
  displayName,
  formatRef,
  parseRef,
  type PortfolioScope,
} from '@promanage/core';
import { Amount, Button, Field, Select } from '@promanage/ui';
import { FormDialog } from '../components/FormDialog';
import { ColumnPicker } from '../features/table/ColumnPicker';
import { DataTable } from '../features/table/DataTable';
import { EmptyState, FilterChips, ListShell } from '../features/table/ListShell';
import { compareRows, type SortState } from '../features/table/columns';
import { useColumnState } from '../features/table/useColumnState';
import { TenanciesNav } from '../features/arrears/TenanciesNav';
import { bondColumns, DEFAULT_BOND_SORT, type BondColumnId } from '../features/bonds/bondColumns';
import {
  BondDetailDialog,
  LodgeBondDialog,
  RecordLodgedBondDialog,
  TopUpBondDialog,
} from '../features/bonds/BondDialogs';
import {
  bondAmounts,
  bondStanding,
  heldForLodging,
  type BondRegisterRow,
  type RegisterTenant,
} from '../features/bonds/types';
import { useSession } from '../features/auth/session';
import { usePortfolioScope } from '../features/portfolio/scope';
import { WorkspaceMenu, useWorkspace } from '../features/workspaces/WorkspaceMenu';
import { workspaceChoice, workspaceTable, workspaceText } from '../features/workspaces/state';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/tenancies/bonds')({
  component: BondsPage,
});

const STORAGE_KEY = 'promanage:bond-columns';

type BondFilter = 'current' | 'unlodged' | 'forms' | 'refunded' | 'all';

const FILTERS: { value: BondFilter; label: string }[] = [
  { value: 'current', label: 'Current' },
  { value: 'unlodged', label: 'Not lodged' },
  { value: 'forms', label: 'Forms in progress' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'all', label: 'All' },
];

const NO_ROWS: BondRegisterRow[] = [];

/**
 * The bond register — every bond the agency looks after, wherever it is.
 *
 * ── THREE PLACES A BOND CAN BE, ONE LIST ─────────────────────────────────────────────────────
 *
 * Held in trust (the agency's statutory clock is running), lodged with Tenancy Services (the
 * register proper: bond number, bond and pet bond, lodged date), or lodged from a tenancy's Bond
 * tab before this page existed and still missing its bond number. The default "Current" view is
 * all three minus the refunded ones, most urgent first.
 *
 * ── A FORM IS FILED HERE AND DONE AT TENANCY SERVICES ────────────────────────────────────────
 *
 * Lodgement, refund, change of tenant, change of landlord: each is written up here, then marked
 * submitted and completed as Tenancy Services acts on it. The integration, when it comes, is what
 * will press those buttons. Nothing here moves trust money except completing a lodgement paid from
 * trust, which is a trust administrator's — the same draw as the tenancy's Bond tab.
 */
function BondsPage() {
  const navigate = useNavigate();
  const session = useSession();
  const roles = session?.roles ?? [];
  const mayManage = canManageProperties(roles);
  const mayDrawTrust = canRefundBond(roles);
  const { scope, setScope, canToggle } = usePortfolioScope();

  const register = useQuery(
    trpc.bond.register.list.queryOptions(
      { scope },
      { meta: { errorTitle: 'Could not load the bond register' }, staleTime: 15_000 },
    ),
  );
  const today = register.data?.today ?? '';
  const all = register.data?.rows ?? NO_ROWS;

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<BondFilter>('current');
  const catalogue = useMemo(() => bondColumns(today), [today]);
  const [sort, setSort] = useState<SortState<BondColumnId>>(DEFAULT_BOND_SORT);
  const columns = useColumnState(STORAGE_KEY, catalogue);
  const workspace = useWorkspace({
    route: '/tenancies/bonds',
    state: { filters: { query, filter, scope }, columns: columns.state.order, sort },
    apply(state) {
      const table = workspaceTable(state, catalogue, DEFAULT_BOND_SORT);
      setQuery(workspaceText(state, 'query'));
      setFilter(
        workspaceChoice(
          state,
          'filter',
          FILTERS.map((option) => option.value),
          'current',
        ),
      );
      if (canToggle) setScope(workspaceChoice(state, 'scope', ['own', 'agency'], 'own'));
      columns.setState(table.columns);
      setSort(table.sort);
    },
  });

  const [openKey, setOpenKey] = useState<string | null>(null);
  const [starting, setStarting] = useState<'lodge' | 'record' | null>(null);

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const asRef = needle === '' ? null : parseRef(needle);
    return all
      .filter((row) => {
        const standing = bondStanding(row, today);
        switch (filter) {
          case 'current':
            return standing !== 'refunded';
          case 'unlodged':
            return (
              standing === 'unlodged' ||
              standing === 'overdue' ||
              standing === 'unregistered' ||
              standing === 'top_up'
            );
          case 'forms':
            return row.openRequests.length > 0;
          case 'refunded':
            return standing === 'refunded';
          case 'all':
            return true;
        }
      })
      .filter((row) => {
        if (needle === '') return true;
        if (asRef) {
          return (
            (asRef.entity === 'tenancy' && asRef.sequence === row.tenancyRefSeq) ||
            (asRef.entity === 'property' && asRef.sequence === row.propertyRefSeq) ||
            row.bondRef.toLowerCase() === formatRef(asRef.entity, asRef.sequence).toLowerCase()
          );
        }
        return [
          row.address,
          row.suburb,
          row.bondRef,
          row.kind === 'lodged' ? row.bondNumber : null,
          ...row.tenants.map((tenant) => tenant.name),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(needle);
      })
      .sort((a, b) => compareRows(a, b, sort, catalogue));
  }, [all, filter, query, sort, catalogue, today]);

  const open = all.find((row) => row.key === openKey) ?? null;

  // Every live bond, whether or not a top-up is waiting on it.
  const lodged = all.filter((row) => row.kind === 'lodged' && row.closedOn === null);
  const overdue = all.filter((row) => bondStanding(row, today) === 'overdue').length;
  const heldTotal = all.reduce((sum, row) => sum + heldForLodging(row), 0n);
  const summary = register.isPending
    ? 'Loading…'
    : [
        `${lodged.length} lodged with Tenancy Services`,
        overdue > 0 ? `${overdue} past the lodgement date` : null,
        all.some((row) => row.kind === 'unregistered') ? 'some missing a bond number' : null,
      ]
        .filter(Boolean)
        .join(' · ');

  const shell = (
    <ListShell
      title="Bonds"
      subtitle={summary}
      nav={<TenanciesNav />}
      meta={
        register.data ? (
          <div className="flex gap-5 leading-tight whitespace-nowrap">
            <div>
              <span className="block text-[0.6875rem] text-ink-subtle">Lodged</span>
              <Amount
                value={lodged.reduce((sum, row) => sum + bondAmounts(row).total, 0n)}
                currency
                className="text-[0.9375rem] font-semibold"
              />
            </div>
            <div>
              <span className="block text-[0.6875rem] text-ink-subtle">Held in trust</span>
              <Amount value={heldTotal} currency className="text-[0.9375rem] font-semibold" />
            </div>
          </div>
        ) : null
      }
      actions={
        <>
          <WorkspaceMenu workspace={workspace} />
          <ColumnPicker
            columns={catalogue}
            state={columns.state}
            onChange={columns.setState}
            onReset={columns.reset}
          />
          {mayManage ? (
            <>
              <Button size="sm" variant="secondary" onClick={() => setStarting('record')}>
                Record lodged bond
              </Button>
              <Button size="sm" onClick={() => setStarting('lodge')}>
                <span aria-hidden="true">+</span> Lodge a bond
              </Button>
            </>
          ) : null}
        </>
      }
      search={query}
      onSearchChange={setQuery}
      searchPlaceholder="Address, tenant, bond number or ref"
      searchLabel="Search bonds"
      filters={<FilterChips label="Show" value={filter} options={FILTERS} onChange={setFilter} />}
      isPending={register.isPending}
      error={register.error}
      onRetry={() => void register.refetch()}
      isEmpty={rows.length === 0}
      empty={
        <EmptyState title={all.length === 0 ? 'No bonds yet' : 'Nothing matches'}>
          {all.length === 0
            ? 'A bond appears here when one is receipted into trust, lodged from this page, or recorded as already lodged with Tenancy Services.'
            : 'Try another filter, or a different address, tenant or bond number.'}
        </EmptyState>
      }
    >
      <DataTable
        rows={rows}
        columns={columns.visible}
        sort={sort}
        onSortChange={setSort}
        rowKey={(row) => row.key}
        onOpen={(row) => setOpenKey(row.key)}
        caption={`Bond register, one row per bond. ${summary}.`}
        minWidth="68rem"
      />
    </ListShell>
  );

  // OUTSIDE the shell, which renders its empty state INSTEAD of its children: inside it, both
  // header buttons were dead on an empty register — exactly when the first bond is recorded.
  return (
    <>
      {shell}
      {open ? (
        <BondDetailDialog
          key={open.key}
          row={open}
          today={today}
          mayManage={mayManage}
          mayDrawTrust={mayDrawTrust}
          onClose={() => setOpenKey(null)}
          onStepUp={() => void navigate({ to: '/mfa' })}
        />
      ) : null}
      {starting ? (
        <StartFromTenancy
          mode={starting}
          register={all}
          today={today}
          scope={scope}
          onClose={() => setStarting(null)}
        />
      ) : null}
    </>
  );
}

/**
 * Lodge a bond, or record one already lodged, for a tenancy that may not be on the register yet —
 * the tenant who paid Tenancy Services directly has no row to click. Choose the tenancy, then the
 * same form the row would have opened.
 */
function StartFromTenancy({
  mode,
  register,
  today,
  scope,
  onClose,
}: {
  mode: 'lodge' | 'record';
  register: readonly BondRegisterRow[];
  today: string;
  scope: PortfolioScope;
  onClose: () => void;
}) {
  const tenancies = useQuery(
    trpc.property.tenancies.queryOptions(
      { scope },
      { meta: { errorTitle: 'Could not load the tenancies' }, staleTime: 30_000 },
    ),
  );
  const [tenancyId, setTenancyId] = useState('');
  const [chosen, setChosen] = useState(false);

  const options = (tenancies.data ?? []).filter(
    (tenancy) => tenancy.endsOn === null || tenancy.endsOn >= today,
  );
  const picked = options.find((tenancy) => tenancy.id === tenancyId);

  if (chosen && picked) {
    const tenants: RegisterTenant[] = picked.people
      .filter((person) => person.roleType === 'tenant')
      .map((person) => ({ personId: person.personId, name: displayName(person), current: true }));
    const held = register.find((row) => row.tenancyId === picked.id && row.kind === 'unlodged');
    // A tenancy whose bond is live is topped up under that bond's number, never lodged twice.
    const live = register.find(
      (row) => row.tenancyId === picked.id && row.kind === 'lodged' && row.closedOn === null,
    );
    if (mode === 'lodge' && live?.kind === 'lodged') {
      return <TopUpBondDialog row={live} onClose={onClose} />;
    }
    return mode === 'lodge' ? (
      <LodgeBondDialog
        tenancyId={picked.id}
        subject={picked.address}
        heldCents={held && held.kind === 'unlodged' ? BigInt(held.heldCents) : 0n}
        tenants={tenants}
        onClose={onClose}
      />
    ) : (
      <RecordLodgedBondDialog
        tenancyId={picked.id}
        subject={picked.address}
        today={today}
        onClose={onClose}
      />
    );
  }

  return (
    <FormDialog
      title={mode === 'lodge' ? 'Lodge a bond' : 'Record a lodged bond'}
      onClose={onClose}
      onSubmit={(event) => {
        event.preventDefault();
        if (picked) setChosen(true);
      }}
    >
      <Field label="Tenancy" required hint="Current tenancies in your view">
        {(props) => (
          <Select
            {...props}
            value={tenancyId}
            disabled={tenancies.isPending}
            onChange={(event) => setTenancyId(event.target.value)}
          >
            <option value="">{tenancies.isPending ? 'Loading…' : 'Choose a tenancy'}</option>
            {options.map((tenancy) => (
              <option key={tenancy.id} value={tenancy.id}>
                {tenancy.address} · {tenancy.ref}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <div className="mt-1 flex gap-2">
        <Button type="submit" disabled={!picked}>
          Continue
        </Button>
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </FormDialog>
  );
}
